import React, { useState } from 'react';
import CalcShell from '../CalcShell';
import { CNetActions, CNetResult, CNetHintBar } from '../other/_cnet';

function gcd(a, b) { a = Math.abs(a); b = Math.abs(b); while (b) { [a, b] = [b, a % b]; } return a || 1; }
function factorize(n) {
  const f = {};
  let x = Math.abs(n);
  for (let d = 2; d * d <= x; d++) while (x % d === 0) { f[d] = (f[d] || 0) + 1; x = x / d; }
  if (x > 1) f[x] = (f[x] || 0) + 1;
  return f;
}
function primeStr(n) {
  const f = factorize(n);
  const parts = Object.entries(f).flatMap(([p, e]) => Array(e).fill(p));
  return parts.length ? parts.join(' × ') : String(n);
}

export default function GCFCalc() {
  const [nums, setNums] = useState('330, 75, 450, 225');
  const [res, setRes] = useState(null);
  const calc = () => {
    const arr = nums.split(/[,\s]+/).map(x => parseInt(x, 10)).filter(x => !Number.isNaN(x));
    if (!arr.length) { setRes({ error: 'Enter at least one integer.' }); return; }
    const g = arr.reduce((a, b) => gcd(a, b));
    // Common primes with minimum exponents
    const facts = arr.map(factorize);
    const primes = Object.keys(facts[0]).filter(p => facts.every(f => f[p] > 0));
    const commonPrimes = primes.map(p => ({ p, e: Math.min(...facts.map(f => f[p])) }));
    setRes({ arr, g, facts, commonPrimes });
  };
  const clear = () => { setNums(''); setRes(null); };
  return (
    <CalcShell showActions={false} look="cnet" title="Greatest Common Factor Calculator" subtitle="Compute the GCF (also called GCD) of two or more positive integers.">
      <p className="text-sm text-slate-700 mb-3">Please provide integers separated by a comma ",", and click the "Calculate" button to find their greatest common factor.</p>
      <CNetHintBar />
      <div className="cnet-panel">
        <textarea data-testid="gcf-nums" className="cnet-input w-full" rows="4" value={nums} onChange={e => setNums(e.target.value)} style={{ height: 'auto', minHeight: 96 }} />
        <CNetActions onCalc={calc} onClear={clear} testId="gcf" />
      </div>
      {res && res.error && <div className="text-red-600 mt-3">{res.error}</div>}
      {res && !res.error && (
        <CNetResult>
          <div className="text-2xl font-semibold text-slate-900" data-testid="gcf-result">
            GCF({res.arr.join(', ')}) = <span className="text-[#4c7b25]">{res.g}</span>
          </div>
          <div className="mt-3 font-bold text-slate-900">Steps:</div>
          <div className="text-slate-800 mt-1">Prime factorization of the numbers:</div>
          <div className="font-mono text-sm mt-1 leading-7" data-testid="gcf-steps">
            {res.arr.map(n => (
              <div key={n}>{n} = {primeStr(n)}</div>
            ))}
          </div>
          <div className="mt-3 font-mono text-sm leading-7">
            <div>GCF({res.arr.join(', ')})</div>
            <div>= {res.commonPrimes.map(({ p, e }) => Array(e).fill(p).join(' × ')).filter(Boolean).join(' × ') || '1'}</div>
            <div>= <b className="text-[#4c7b25]">{res.g}</b></div>
          </div>
        </CNetResult>
      )}
    </CalcShell>
  );
}
